import { hexToRgb, relativeLuminance, rgbToHex, type RgbColour } from './productColour';

export interface TonePalette {
  light: string;
  base: string;
  highlight: string;
  mid: string;
  shadow: string;
  deepShadow: string;
  edge: string;
  mitre: string;
  softShadow: string;
}

function mixRgb(a: RgbColour, b: RgbColour, amount: number): RgbColour {
  const t = Math.max(0, Math.min(1, amount));
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function mixHex(hex: string, toward: string, amount: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(toward);
  if (!a || !b) return hex.toUpperCase();
  return rgbToHex(mixRgb(a, b, amount));
}

const WHITE = '#FFFFFF';
const NEAR_BLACK = '#121212';

/**
 * Derive a readable tonal set from one base HEX. Dark colours still get
 * lifted highlights so chamfer/ovolo edges stay visible. Never collapse
 * every tone to pure black.
 */
export function generateTonePalette(baseHex: string): TonePalette {
  const baseRgb = hexToRgb(baseHex);
  const base = baseRgb ? rgbToHex(baseRgb) : '#F1F0EA';
  const luminance = relativeLuminance(base);
  const isDark = luminance < 0.18;
  const highlightMix = isDark ? 0.42 : 0.58;
  const lightMix = isDark ? 0.22 : 0.32;
  const midMix = isDark ? 0.18 : 0.12;
  const shadowMix = isDark ? 0.32 : 0.28;
  const deepMix = isDark ? 0.48 : 0.46;

  const highlight = mixHex(base, WHITE, highlightMix);
  const light = mixHex(base, WHITE, lightMix);
  const mid = mixHex(base, NEAR_BLACK, midMix);
  let shadow = mixHex(base, NEAR_BLACK, shadowMix);
  let deepShadow = mixHex(base, NEAR_BLACK, deepMix);
  if (shadow === base) shadow = mixHex(base, '#000000', 0.35);
  if (deepShadow === base || deepShadow === shadow) {
    deepShadow = mixHex(shadow, '#000000', 0.35);
  }

  return {
    light,
    base,
    highlight,
    mid,
    shadow,
    deepShadow,
    softShadow: shadow,
    edge: mixHex(base, NEAR_BLACK, isDark ? 0.4 : 0.38),
    mitre: deepShadow,
  };
}

/** Fallback when no threshold finish id is resolved. Matches library `aluminium`. */
export const DEFAULT_THRESHOLD_BASE_HEX = '#C9C9C7';
/** Locked rubber tones. Gasket is not a customer colour surface. */
export const DEFAULT_GASKET_STROKE = '#3F3F3D';
export const DEFAULT_GASKET_FILL = '#3E3E3C';
export const DEFAULT_CILL_HEX = '#8A8175';

export interface GasketColours {
  stroke: string;
  fill: string;
}

export interface ComponentColourPalettes {
  frame: TonePalette;
  threshold: TonePalette;
  gasket: GasketColours;
  cill: TonePalette;
}

export function generateComponentColourPalettes(args: {
  frameBaseHex: string;
  thresholdBaseHex?: string;
  gasket?: Partial<GasketColours>;
  cillHex?: string;
}): ComponentColourPalettes {
  return {
    frame: generateTonePalette(args.frameBaseHex),
    threshold: generateTonePalette(args.thresholdBaseHex ?? DEFAULT_THRESHOLD_BASE_HEX),
    gasket: {
      stroke: args.gasket?.stroke ?? DEFAULT_GASKET_STROKE,
      fill: args.gasket?.fill ?? DEFAULT_GASKET_FILL,
    },
    cill: generateTonePalette(args.cillHex ?? DEFAULT_CILL_HEX),
  };
}
